import {observer} from 'mobx-react-lite'
import React, {useEffect, useRef} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {CompanyPreferencesClient} from '@client/company-preferences-client'
import {mouse} from '@common/mouse'
import {Tracking, TrackingEvent} from '@common/tracking'
import {isCollapsedHeader} from '@components/board/general-board-settings-popup/collapsed-header-checkbox'
import {LinkButtonNew} from '@components/common/base/buttons'
import {BgOpacityContainer} from '@components/common/bg-opacity-container/bg-opacity-container'
import {Tooltip} from '@components/common/tooltip/tooltip'
import {crmStore} from '@components/crm/crm-store'
import {multiActionsWithTasksStore} from '@components/multi-actions-with-tasks/multi-actions-with-tasks-store'
import {TimelineControls} from '@components/timeline-new/timeline-controls'
import {useBoard, useLocationUpdateEffect} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {boardViewStore} from './board-view-store'

const BoardViewTw = tw.div<{$collapsed?: boolean; $ganttView?: boolean}>`
  z-1000
  absolute
  left-16
  flex
  gap-10
  justify-between
  ${p => (p.$collapsed ? 'top-52' : 'top-[92px]')}
  ${p => (p.$ganttView ? 'w-full' : '')}
`

const BtnTw = tw.div<{$disabled?: boolean; $fontBold?: boolean}>`
  bg-main-old
  h-20
  rounded-2
  mr-6
  leading-5
  text-14
  text-secondary-old
  px-8
  min-w-20
  text-center
  cursor-pointer
  hover:text-main-old
  active:bg-main-header-old
  active:text-on-color
  ${p =>
    p.$disabled
      ? `
    bg-on-color
    text-obscure-old
    cursor-default
    hover:text-obscure-old
    active:bg-on-color
    active:text-obscure-old
  `
      : ''}
  ${p => (p.$fontBold ? `font-semibold` : '')}
`

const TwTimelineBoardInner = tw.div`
  flex
  p-4
  gap-2
  bg-background-sticker-panel
  rounded-8
  h-32
`

export const BoardView = observer(() => {
  const {view} = boardViewStore.state

  const board = useBoard()
  useEffect(() => {
    boardViewStore.updateDefaultView()
  }, [board?.id])
  const refCollapsed = useRef<boolean>(false)

  const {useNewGantt} = CompanyPreferencesClient.get(globals.getCompanyId())

  useLocationUpdateEffect(() => {
    const collapsedHeader = isCollapsedHeader()
    if (collapsedHeader !== refCollapsed.current) {
      refCollapsed.current = collapsedHeader
      return true
    }

    return false
  })

  const ganttView = view === 'timeline'
  const newGanttView = view === 'timeline' && useNewGantt

  const isCrmBoard = crmStore.isCrmBoard(board)

  return (
    <BoardViewTw $collapsed={refCollapsed.current} $ganttView={newGanttView}>
      <TwTimelineBoardInner>
        <Tooltip content={Lang.context.board.boardViewHint}>
          {/* TODO: avoid if/else here, rework with <DefaultProjectButtons> and <CrmProjectButtons>, so we can extend it in the future as <*ProjectButtons */}
          <LinkButtonNew
            onClick={setBoard}
            color="view/tab"
            icon={isCrmBoard ? 'IconCustomBoardFunnel' : 'IconCustomKanban'}
            iconViewBox="0 0 16 16"
            title={Lang.context.board.buttonBoard}
            forcedState={view === 'board' ? 'active' : undefined}
          />
        </Tooltip>
        {isCrmBoard ? (
          <>
            <Tooltip content={Lang.msg.crm.activities.viewHint}>
              <LinkButtonNew
                onClick={setActivities}
                color="view/tab"
                icon="IconCustomBolt"
                iconViewBox="0 0 16 16"
                title={Lang.msg.crm.activities.boardViewLabel}
                forcedState={view === 'activities' ? 'active' : undefined}
              />
            </Tooltip>
            <Tooltip content={Lang.msg.crm.analytics.viewHint}>
              <LinkButtonNew
                onClick={setAnalytics}
                color="view/tab"
                icon="IconCustomBoardAnalytics"
                iconViewBox="0 0 16 16"
                title={Lang.msg.crm.analytics.buttonTimeline}
                forcedState={view === 'analytics' ? 'active' : undefined}
              />
            </Tooltip>
          </>
        ) : (
          <>
            <Tooltip content={Lang.msg.timeline.timelineViewHint}>
              <LinkButtonNew
                onClick={setTimeline}
                color="view/tab"
                icon="IconCustomGant"
                iconViewBox="0 0 16 16"
                title={Lang.msg.timeline.buttonGantt}
                forcedState={ganttView ? 'active' : undefined}
              />
            </Tooltip>
            <Tooltip content={Lang.msg.timeline.calendarViewHint}>
              <LinkButtonNew
                onClick={setCalendar}
                color="view/tab"
                icon="IconCustomBoardCalendar"
                iconViewBox="0 0 16 16"
                title={Lang.msg.timeline.buttonCalendar}
                forcedState={view === 'calendar' ? 'active' : undefined}
              />
            </Tooltip>
          </>
        )}
      </TwTimelineBoardInner>

      {newGanttView && <TimelineControls />}

      {ganttView && !useNewGantt && (
        <BgOpacityContainer
          className="absolute flex px-8 py-6 rounded-4 left-8 items-center"
          bgOpacityClassName="opacity-50"
          bgColorClassName="bg-main-old rounded-4"
        >
          <div className="flex">
            <BtnTw
              style={{
                boxShadow: disallowedChange(0.1)
                  ? 'inset 0 0 6px -3px bg-main-header-old'
                  : ''
              }}
              $fontBold
              $disabled={disallowedChange(0.1)}
              onClick={changeZoom(0.1)}
            >
              +
            </BtnTw>
            <BtnTw
              style={{
                boxShadow: disallowedChange(-0.1)
                  ? 'inset 0 0 6px -3px bg-main-header-old'
                  : ''
              }}
              $fontBold
              $disabled={disallowedChange(-0.1)}
              onClick={changeZoom(-0.1)}
            >
              –
            </BtnTw>
            <BtnTw onClick={goToToday}>{Lang.msg.common.today}</BtnTw>
          </div>
        </BgOpacityContainer>
      )}
    </BoardViewTw>
  )
})

const setBoard = () => {
  !mouse.dragItem && multiActionsWithTasksStore.visibleOff()
  boardViewStore.setView('board')
  if (crmStore.isCurrentProjectCrm) {
    void Tracking.send(TrackingEvent.viewSwitchToTheCrmFunnel)
  } else {
    void Tracking.send(TrackingEvent.viewSwitchToTheBoardView)
  }
}
const setTimeline = () => {
  multiActionsWithTasksStore.visibleOff()
  boardViewStore.setView('timeline')
  void Tracking.send(TrackingEvent.viewSwitchToTheGanttChartView)
}
const setCalendar = () => {
  multiActionsWithTasksStore.visibleOff()
  boardViewStore.setView('calendar')
  void Tracking.send(TrackingEvent.viewSwitchToTheCalendarView)
}

const setAnalytics = () => {
  multiActionsWithTasksStore.visibleOff()
  boardViewStore.setView('analytics')
  void Tracking.send(TrackingEvent.viewSwitchToTheCrmAnalytics)
}

const setActivities = () => {
  multiActionsWithTasksStore.visibleOff()
  boardViewStore.setView('activities')
  void Tracking.send(TrackingEvent.viewSwitchToTheCrmActivity)
}

const goToToday = () => {
  const cnt = document.querySelector<HTMLElement>('.timeline__cnt')
  if (!cnt) {
    return
  }
  const today = cnt.querySelector<HTMLElement>('.timeline__today')
  if (!today) {
    return
  }
  cnt.scrollLeft = today.offsetLeft - cnt.offsetWidth / 2
}

const disallowedChange = (change: number) => {
  const {zoom} = boardViewStore.state
  return zoom + change > 1.25 || zoom + change < 0.55
}

const changeZoom = (change: number) => () => {
  if (!disallowedChange(change)) {
    boardViewStore.setZoom(boardViewStore.state.zoom + change)
  }
}
